// Anonymous client/session identifiers, independent of login state.
//
// - clientId: persistent per browser profile (localStorage) — links activity
//   from the same device/person across visits.
// - sessionId: per browsing session (sessionStorage) — new tab/window or a
//   closed-and-reopened browser starts a fresh session.
//
// Both are attached to every API request as X-Client-Id / X-Session-Id (see
// services/api.ts), so feedback submissions — and the upcoming telemetry
// feature — can be linked to the same visitor without requiring an account.

const CLIENT_ID_KEY = 'chc_client_id';
const SESSION_ID_KEY = 'chc_session_id';

// In-memory fallbacks so ids still exist for the page's lifetime when web
// storage is unavailable (private mode restrictions, blocked cookies, etc.).
let memoryClientId: string | null = null;
let memorySessionId: string | null = null;

function newId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  }
}

function getOrCreate(storage: Storage, key: string): string {
  const existing = storage.getItem(key);
  if (existing) return existing;
  const id = newId();
  storage.setItem(key, id);
  return id;
}

export function getClientId(): string {
  try {
    return getOrCreate(window.localStorage, CLIENT_ID_KEY);
  } catch {
    memoryClientId ??= newId();
    return memoryClientId;
  }
}

export function getSessionId(): string {
  try {
    return getOrCreate(window.sessionStorage, SESSION_ID_KEY);
  } catch {
    memorySessionId ??= newId();
    return memorySessionId;
  }
}
